import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, AlertCircle, ExternalLink, CheckCircle2, Sparkles } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useRole } from "@/hooks/use-role";
import {
  createConnectOnboardingLink,
  refreshConnectStatus,
} from "@/lib/stripe-connect.functions";
import {
  StudioUploader,
  analysisFile,
  readyAssets,
  studioBusy,
  type StudioAsset,
} from "@/components/sell/studio-uploader";

import type { ConfirmedScanFields } from "@/components/scan-card-dialog";
import type { IdentityApplyPayload } from "@/components/sell/ai-scan-panel";
import type { GradeApplyPayload } from "@/components/sell/ai-grade-panel";

// AI Scan 2.0 identity panel — lazy so its taxonomy tables stay out of the
// initial /sell bundle.
const ProcessingTimeline = lazy(() =>
  import("@/components/sell/processing-timeline").then((m) => ({
    default: m.ProcessingTimeline,
  })),
);

const AiScanPanel = lazy(() =>
  import("@/components/sell/ai-scan-panel").then((m) => ({ default: m.AiScanPanel })),
);

// AI Scan 3.0 condition & grading panel — lazy for the same reason.
const AiGradePanel = lazy(() =>
  import("@/components/sell/ai-grade-panel").then((m) => ({ default: m.AiGradePanel })),
);



// The AI scan dialog pulls in browser-side image processing (deskew,
// hashing, quality analysis). Load it only when the seller opens it so it
// never costs the initial /sell bundle.
const ScanCardDialog = lazy(() =>
  import("@/components/scan-card-dialog").then((m) => ({ default: m.ScanCardDialog })),
);
import { ShippingRecommendationCard } from "@/components/shipping-recommendation-card";
import { BundleSuggestionsPanel } from "@/components/bundle-suggestions-panel";
import { PriceEstimatePanel } from "@/components/price-estimate-panel";
import { rewriteDescription } from "@/lib/description-ai.functions";
import { ProfileCompletionGate } from "@/components/profile-completion-gate";
import { GradingCertLookup } from "@/components/grading-cert-lookup";
import { runAuctionInspection } from "@/lib/inspection/inspection.functions";
import { buildAuctionSlug } from "@/lib/auction-slug";
import { checkListingRegistry, linkListingRegistry } from "@/lib/registry/registry.functions";
import type { RegistryMismatch } from "@/lib/registry/registry";

type RegistryCheck = {
  found: boolean;
  blocked: boolean;
  registryId: string | null;
  registryPath: string | null;
  badgeState: string;
  provider: string | null;
  certificateNumber: string | null;
  cardName: string | null;
  setName: string | null;
  grade: string | null;
  officialUrl: string | null;
  lastVerifiedAt: string | null;
  mismatches: RegistryMismatch[];
  reasons: string[];
  duplicateActiveListings: number;
};

type ConfirmedScanField = keyof ConfirmedScanFields;

export const Route = createFileRoute("/sell")({
  // Client-only: the Supabase session lives in localStorage, so the auth gate
  // below cannot run during SSR. Without this, a hard refresh or deep link to
  // /sell redirected signed-in sellers to /auth. Protection is unchanged —
  // beforeLoad still runs (client-side) on every entry to the route.
  ssr: false,
  head: () => ({
    meta: [
      { title: "List a card — BidNova Auctions" },
      { name: "description", content: "List your graded Pokémon cards for auction on BidNova Auctions. GBP payouts via Stripe, UK Royal Mail Special Delivery." },
      { name: "robots", content: "noindex, follow" },
      { property: "og:title", content: "List a card — BidNova Auctions" },
      { property: "og:description", content: "Sell graded Pokémon cards to UK buyers on BidNova Auctions." },
      { property: "og:url", content: "https://bidnovaauctions.uk/sell" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://bidnovaauctions.uk/sell" }],
  }),
  beforeLoad: async ({ location }) => {
    const { data } = await supabase.auth.getUser();
    if (!data.user) throw redirect({ to: "/auth", search: { mode: "signin", redirect: location.href } });
  },
  component: SellPage,
});

import {
  ALL_GRADERS,
  GRADER_LABEL,
  GRADE_OPTIONS,
  CARD_TYPES,
  CARD_TYPE_LABEL,
  PRODUCT_TYPES,
  PRODUCT_TYPE_LABEL,
  ACCESSORY_CATEGORIES,
  ACCESSORY_CATEGORY_LABEL,
  CONDITION_LABEL,
  COMPATIBLE_GAMES,
  conditionsForProductType,
  defaultConditionFor,
  type CardType,
  type Grader,
  type ProductType,
  type AccessoryCategory,
  CARD_LANGUAGES,
  FINISH_OPTIONS,
  EDITION_OPTIONS,
  SELLER_GRADING_COMPANIES,
  isApiGrader,
  supportsCertVerification,
  type ApiGrader,
  SELLER_GRADE_OPTIONS,
  formatGradeLabel,
  TCG_GAMES,
  RARITY_OPTIONS,
} from "@/lib/grading/companies";
import { BrandCombobox } from "@/components/brand-combobox";
import { subcategoriesFor, attributeFieldsFor, categoryForProductType, validateAttributes, cleanAttributes } from "@/lib/marketplace/categories";
import { cleanBrandInput } from "@/lib/brand-suggestions";

const DURATIONS = [
  { label: "1 day", hours: 24 },
  { label: "3 days", hours: 72 },
  { label: "5 days", hours: 120 },
  { label: "7 days", hours: 168 },
];
// Admin-only short durations for internal testing. Never exposed to normal sellers.
const ADMIN_TEST_DURATIONS = [
  { label: "5 minutes (test)", hours: 5 / 60 },
  { label: "15 minutes (test)", hours: 15 / 60 },
  { label: "1 hour (test)", hours: 1 },
];

type StripeStatus = {
  id: string;
  stripe_account_id: string | null;
  stripe_charges_enabled: boolean | null;
  stripe_payouts_enabled: boolean | null;
  stripe_details_submitted: boolean | null;
};

function StripeGate({ userId, profile, onRefreshed }: { userId: string; profile: StripeStatus | null; onRefreshed: () => void }) {
  const startOnboarding = useServerFn(createConnectOnboardingLink);
  const refresh = useServerFn(refreshConnectStatus);
  const started = !!profile?.stripe_account_id;

  const onboard = useMutation({
    mutationFn: async () =>
      startOnboarding({ data: { origin: window.location.origin, returnPath: "/sell" } }),
    onSuccess: (r) => { if (r?.url) window.location.href = r.url; },
    onError: (e: Error) => toast.error(e.message),
  });

  const sync = useMutation({
    mutationFn: async () => refresh({}),
    onSuccess: () => { onRefreshed(); toast.success("Stripe status updated"); },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-12">
      <div className="rounded-2xl border border-amber-500/40 bg-amber-500/5 p-8 gold-ring">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-6 w-6 text-amber-400 shrink-0 mt-1" />
          <div className="flex-1">
            <h1 className="font-display text-3xl">Complete Stripe onboarding to list</h1>
            <p className="text-sm text-muted-foreground mt-2">
              BidNova Auctions pays sellers via Stripe Connect. You need a Stripe Express account with charges and payouts enabled before you can publish an auction. It takes just a few minutes.
            </p>

            <div className="mt-5 grid gap-2 sm:grid-cols-3 text-xs text-muted-foreground">
              <div className="rounded-lg bg-surface-2 px-3 py-2 flex items-center gap-2">
                {profile?.stripe_details_submitted ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> : <AlertCircle className="h-3.5 w-3.5 text-amber-400" />}
                Details submitted
              </div>
              <div className="rounded-lg bg-surface-2 px-3 py-2 flex items-center gap-2">
                {profile?.stripe_charges_enabled ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> : <AlertCircle className="h-3.5 w-3.5 text-amber-400" />}
                Charges enabled
              </div>
              <div className="rounded-lg bg-surface-2 px-3 py-2 flex items-center gap-2">
                {profile?.stripe_payouts_enabled ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> : <AlertCircle className="h-3.5 w-3.5 text-amber-400" />}
                Payouts enabled
              </div>
            </div>


            <div className="mt-6 flex gap-3 flex-wrap">
              <Button
                onClick={() => onboard.mutate()}
                disabled={onboard.isPending}
                size="lg"
                className="bg-gold-gradient hover:opacity-90"
              >
                {onboard.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <ExternalLink className="h-4 w-4 mr-2" />
                    {started ? "Continue Stripe onboarding" : "Start Stripe onboarding"}
                  </>
                )}
              </Button>
              {started && (
                <Button
                  variant="outline"
                  className="border-border/60"
                  onClick={() => sync.mutate()}
                  disabled={sync.isPending}
                >
                  {sync.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Refresh status"}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SellPage() {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, refetch: refetchRoles } = useRole();
  // Refetch roles on mount so a freshly-granted admin sees the test toggle without a hard refresh.
  useEffect(() => { refetchRoles(); }, [refetchRoles]);
  // Upload diagnostics are admin-only AND opt-in (?imaging-debug=1, remembered per tab).
  const [imagingDebug, setImagingDebug] = useState(false);
  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get("imaging-debug");
    if (q === "1") sessionStorage.setItem("bidnova.imaging-debug", "1");
    if (q === "0") sessionStorage.removeItem("bidnova.imaging-debug");
    setImagingDebug(sessionStorage.getItem("bidnova.imaging-debug") === "1");
  }, []);
  const navigate = useNavigate();
  const runInspectionFn = useServerFn(runAuctionInspection);
  const refresh = useServerFn(refreshConnectStatus);
  const [studioAssets, setStudioAssets] = useState<StudioAsset[]>([]);

  const [durationHours, setDurationHours] = useState(72);
  const [isTest, setIsTest] = useState(false);
  const [condition, setCondition] = useState<string>("near_mint");
  const [productType, setProductType] = useState<ProductType>("trading_card");
  const [accessoryCategory, setAccessoryCategory] = useState<AccessoryCategory>("sleeves");
  const [cardType, setCardType] = useState<CardType>("graded");
  const [grader, setGrader] = useState<Grader>("PSA");
  const [customGrader, setCustomGrader] = useState<string>("");
  const [customGrade, setCustomGrade] = useState<string>("");
  // Sealed / Accessory fields
  const [brand, setBrand] = useState<string>("");
  const [subcategory, setSubcategory] = useState<string>("");
  const [attrs, setAttrs] = useState<Record<string, string | boolean>>({});
  const [productLineSet, setProductLineSet] = useState<string>("");
  const [quantity, setQuantity] = useState<string>("1");
  const [compatibleGame, setCompatibleGame] = useState<string>("");
  const [customCompatibleGame, setCustomCompatibleGame] = useState<string>("");
  const [buyNowEnabled, setBuyNowEnabled] = useState(false);
  const [buyNowPounds, setBuyNowPounds] = useState("");
  const [keepBuyNowAfterBid, setKeepBuyNowAfterBid] = useState(false);
  const [autoRelist, setAutoRelist] = useState(true);
  // AI-scanner controlled fields + dirty tracking so seller edits are never overwritten.
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [cardSet, setCardSet] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [grade, setGrade] = useState("");
  const [cardLanguage, setCardLanguage] = useState<string>("English");
  const [finish, setFinish] = useState<string>("");
  const [edition, setEdition] = useState<string>("Unlimited");
  // Trading Card professional attributes
  const [tcgGame, setTcgGame] = useState<string>("pokemon");
  const [tcgGameOther, setTcgGameOther] = useState<string>("");
  const [rarity, setRarity] = useState<string>("");
  const [rarityOther, setRarityOther] = useState<string>("");
  const [isAutographed, setIsAutographed] = useState<boolean>(false);
  const [autographDetails, setAutographDetails] = useState<string>("");
  const [isSerialNumbered, setIsSerialNumbered] = useState<boolean>(false);
  const [serialNumber, setSerialNumber] = useState<string>("");
  const [startingPricePounds, setStartingPricePounds] = useState("");
  const [certNumber, setCertNumber] = useState("");
  const [certVerified, setCertVerified] = useState(false);
  const [certMeta, setCertMeta] = useState<{
    verified: boolean;
    status: string;
    verified_at: string | null;
    official_url: string;
    method: string;
  } | null>(null);
  // Verified Card Registry: publish-time check result for the entered cert.
  const [registryCheck, setRegistryCheck] = useState<RegistryCheck | null>(null);
  const [registryConfirmed, setRegistryConfirmed] = useState(false);
  const checkRegistryFn = useServerFn(checkListingRegistry);
  const linkRegistryFn = useServerFn(linkListingRegistry);

  const [scanOpen, setScanOpen] = useState(false);
  const [dirtyFields, setDirtyFields] = useState<Set<ConfirmedScanField>>(new Set());
  const [rewriting, setRewriting] = useState(false);
  const rewriteFn = useServerFn(rewriteDescription);

  const markDirty = (k: ConfirmedScanField) =>
    setDirtyFields((prev) => (prev.has(k) ? prev : new Set(prev).add(k)));

  // ---- AI Scan 2.0: automatic identity extraction from the first photo ----
  // Always the seller's latest edit, so OCR / identity / grading analyse the
  // same image the listing will show.
  const heroAsset = studioAssets[0] ?? null;
  const scanSourceFile = analysisFile(heroAsset);
  // Bumped whenever the first photo is re-processed (a manual crop, a rotate,
  // or a retry) so every AI panel re-scans the corrected image.
  const scanRevisionRef = useRef(0);
  const lastRenderRef = useRef<string | null>(null);
  const firstRender = `${heroAsset?.afterUrl ?? ""}|${heroAsset?.editVersion ?? 0}`;
  if (firstRender !== lastRenderRef.current) {
    lastRenderRef.current = firstRender;
    if (heroAsset?.afterUrl || heroAsset?.editVersion) scanRevisionRef.current += 1;
  }
  const scanRevision = scanRevisionRef.current;


  const applyScanIdentity = (fields: IdentityApplyPayload) => {
    if (fields.card_name) {
      setTitle(fields.card_name);
      markDirty("title");
    }
    if (fields.set_name) {
      setCardSet(fields.set_name);
      markDirty("card_set");
    }
    if (fields.card_number) {
      setCardNumber(fields.card_number);
      markDirty("card_number");
    }
    if (fields.game) {
      const known = (TCG_GAMES as readonly { value: string }[]).some(
        (g) => g.value === fields.game,
      );
      setTcgGame(known ? fields.game : "other");
      if (!known) setTcgGameOther(fields.game);
    }
    if (fields.rarity) setRarity(fields.rarity);
    if (fields.language) setCardLanguage(fields.language);
    if (fields.edition) setEdition(fields.edition);
  };

  // ---- AI Scan 3.0: condition & grading estimate ----
  const applyGradeEstimate = (payload: GradeApplyPayload) => {
    if (payload.condition) {
      const opts = conditionsForProductType(productType) as readonly string[];
      if (opts.includes(payload.condition)) {
        setCondition(payload.condition);
        markDirty("condition_form");
      }
    }
    if (payload.grade && cardType === "graded") setGrade(payload.grade);
  };





  // Snap `condition` to a valid option when the product type changes so the
  // insert never sends a condition that isn't part of the current option set.
  useEffect(() => {
    const opts = conditionsForProductType(productType) as readonly string[];
    if (!opts.includes(condition)) {
      setCondition(defaultConditionFor(productType));
      markDirty("condition_form");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productType]);


  async function handleAiRewrite() {
    setRewriting(true);
    try {
      const result = await rewriteFn({
        data: {
          card_name: title || null,
          card_set: cardSet || null,
          card_number: cardNumber || null,
          condition: condition || null,
          grading_company: grader === "RAW" ? null : grader,
          grade: grade || null,
          seller_notes: description || null,
        },
      });
      setDescription(result.description);
      markDirty("description");
      toast.success(result.source === "ai" ? "AI description ready — review and edit before publishing." : "Draft description generated.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "AI rewrite failed");
    } finally {
      setRewriting(false);
    }
  }


  const { data: sellerProfile, isPending: profilePending, isFetching, refetch } = useQuery({
    queryKey: ["sell-stripe-gate", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id,stripe_account_id,stripe_charges_enabled,stripe_payouts_enabled,stripe_details_submitted")
        .eq("id", user!.id)
        .maybeSingle();
      if (error) throw error;
      console.info("[/sell] profile fetched before render", {
        "user.id": user!.id,
        "profile.id": data?.id ?? null,
        stripe_details_submitted: data?.stripe_details_submitted ?? null,
        stripe_charges_enabled: data?.stripe_charges_enabled ?? null,
        stripe_payouts_enabled: data?.stripe_payouts_enabled ?? null,
      });
      return data as StripeStatus | null;
    },
  });

  // Auto-refresh Stripe status when the seller returns from onboarding.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("stripe") === "return" || params.get("stripe") === "refresh") {
      refresh({}).then(() => refetch()).catch(() => {});
      const url = new URL(window.location.href);
      url.searchParams.delete("stripe");
      window.history.replaceState({}, "", url.toString());
    }
  }, [refresh, refetch]);

  const verified =
    !!sellerProfile &&
    sellerProfile.id === user?.id &&
    sellerProfile.stripe_details_submitted === true &&
    sellerProfile.stripe_charges_enabled === true &&
    sellerProfile.stripe_payouts_enabled === true;

  useEffect(() => {
    if (authLoading || !user || profilePending || isFetching) return;
    console.info("[/sell] render decision", {
      "user.id": user.id,
      "profile.id": sellerProfile?.id ?? null,
      stripe_details_submitted: sellerProfile?.stripe_details_submitted ?? null,
      stripe_charges_enabled: sellerProfile?.stripe_charges_enabled ?? null,
      stripe_payouts_enabled: sellerProfile?.stripe_payouts_enabled ?? null,
      render: verified ? "listing_form" : "StripeGate",
    });
  }, [authLoading, user, profilePending, isFetching, sellerProfile, verified]);

  const mutation = useMutation({
    mutationFn: async (form: FormData) => {
      if (!user) throw new Error("Sign in first");
      if (!verified) throw new Error("You must complete Stripe verification before listing an auction.");
      if (studioBusy(studioAssets)) throw new Error("Images are still processing — hang on a moment");
      if (readyAssets(studioAssets).length === 0) throw new Error("Add at least one image");

      const titleValue = String(form.get("title") ?? "").trim();
      const descriptionValue = String(form.get("description") ?? "").trim();
      const gradeValue = grade.trim() || null;
      const starting = Number(form.get("starting_price"));
      const reserve = form.get("reserve_price") ? Number(form.get("reserve_price")) : null;
      const buyNow = buyNowEnabled ? Number(buyNowPounds) : null;
      if (!titleValue) throw new Error("Enter a listing title.");
      if (!Number.isFinite(starting) || starting <= 0) throw new Error("Valid starting price required");
      // Product-type-specific validation.
      const isTradingCard = productType === "trading_card";
      const isSealed = productType === "sealed_product";
      const isAccessory = productType === "accessory";
      const isGeneric = productType === "collectible" || productType === "fashion";
      const mainCategory = categoryForProductType(productType);
      const attrFields = isGeneric ? attributeFieldsFor(mainCategory, subcategory) : [];
      const brandClean = cleanBrandInput(brand);
      const lineSetClean = productLineSet.trim();
      const qtyNumber = Math.max(1, Math.floor(Number(quantity) || 0));
      const compatibleGameClean =
        compatibleGame === "Other" ? customCompatibleGame.trim() : (compatibleGame || "").trim();

      if (isTradingCard) {
        if (!tcgGame) throw new Error("Select a trading card game.");
        if (tcgGame === "other" && !tcgGameOther.trim()) throw new Error("Enter the trading card game.");
        if (!cardSet.trim()) throw new Error("Enter the card set.");
        if (!cardNumber.trim()) throw new Error("Enter the card number.");
        if (!cardLanguage) throw new Error("Select the card language.");
        if (!finish) throw new Error("Select the finish.");
        if (rarity === "other" && !rarityOther.trim()) throw new Error("Enter the rarity.");
        if (isSerialNumbered && !serialNumber.trim()) throw new Error("Enter the serial number.");
        if (cardType === "graded") {
          if (!grader) throw new Error("Select a grading company.");
          if (grader === "OTHER" && !customGrader.trim()) throw new Error("Enter the grading company name.");
          if (!gradeValue || gradeValue === "Ungraded") throw new Error("Select a grade.");
          if (gradeValue === "Other" && !customGrade.trim()) throw new Error("Enter the grade.");
        }
      }
      if (isSealed) {
        if (!lineSetClean) throw new Error("Enter the product line or set.");
        if (!brandClean) throw new Error("Select or enter a brand.");
        if (!Number.isFinite(qtyNumber) || qtyNumber < 1) throw new Error("Quantity must be at least 1.");
      }
      if (isAccessory) {
        if (!brandClean) throw new Error("Select or enter a brand.");
        if (!accessoryCategory) throw new Error("Select an accessory category.");
        if (compatibleGame === "Other" && !customCompatibleGame.trim()) {
          throw new Error("Enter the compatible game.");
        }
      }
      if (isGeneric) {
        if (!subcategory) throw new Error("Select a subcategory.");
        if (productType === "fashion" && !cleanBrandInput(brand)) throw new Error("Enter the brand.");
        const attrErr = validateAttributes(attrFields, attrs);
        if (attrErr) throw new Error(attrErr);
      }
      const validConditions = conditionsForProductType(productType) as readonly string[];
      if (!validConditions.includes(condition)) {
        throw new Error("Select a valid condition for this product type.");
      }

      if (buyNowEnabled) {
        if (!Number.isFinite(buyNow) || (buyNow as number) <= 0) {
          throw new Error("Enter a valid Buy It Now price");
        }
        if ((buyNow as number) <= starting) {
          throw new Error("Buy It Now price must be higher than the starting price");
        }
      }



      // Images were uploaded asynchronously by the studio pipeline. Each asset
      // already has a stable URL that serves the uploaded photo now and the
      // processed studio render as soon as it is ready.
      const ready = readyAssets(studioAssets);
      if (ready.length === 0) throw new Error("Add at least one image");
      const uploadedUrls = ready.map((a) => a.handle!.url);



      const now = new Date();
      const end = new Date(now.getTime() + durationHours * 3600_000);
      const useTest = isAdmin && isTest;
      const finalTitle = useTest ? `[TEST] ${titleValue}` : titleValue;

      // NOT NULL columns (card_set / card_number) must always receive a value.
      // For sealed & accessory listings we use the product-line / brand or an
      // empty placeholder so the DB constraint isn't violated.
      const cardSetPayload = isTradingCard
        ? cardSet.trim()
        : isSealed
        ? lineSetClean
        : brandClean || "";
      const cardNumberPayload = isTradingCard ? cardNumber.trim() : "";

      // Verified Card Registry publish-time validation. Runs only when the
      // seller entered a certificate from a supported grader; a registry
      // outage never blocks publishing.
      const certForRegistry =
        isTradingCard && cardType === "graded" && isApiGrader(grader) ? certNumber.trim() : "";
      let registryLink: { provider: string; cert: string } | null = null;
      if (certForRegistry) {
        let check: RegistryCheck | null = null;
        try {
          check = await checkRegistryFn({
            data: {
              provider: grader.toLowerCase(),
              certNumber: certForRegistry,
              listing: {
                card_name: titleValue,
                set_name: cardSetPayload || null,
                card_number: cardNumberPayload || null,
                grade: gradeValue || null,
                language: cardLanguage || null,
                finish: finish || null,
                serial_number: isSerialNumbered ? serialNumber.trim() || null : null,
              },
            },
          });
        } catch {
          check = null; // registry unavailable — continue without linkage
        }
        if (check) {
          setRegistryCheck(check);
          if (check.blocked) {
            throw new Error(
              `This listing can't be published yet: ${check.reasons[0] ?? "the certificate failed registry checks."}`,
            );
          }
          if (check.found && check.mismatches.length && !registryConfirmed) {
            throw new Error(
              "Your details differ from the official registry record. Review the highlighted differences and tick the confirmation before publishing.",
            );
          }
          if (check.found) registryLink = { provider: grader.toLowerCase(), cert: certForRegistry };
        }
      }


      const { data, error } = await supabase
        .from("auctions")
        .insert({
          seller_id: user.id,
          title: finalTitle,
          description: descriptionValue,
          images: uploadedUrls,
          card_set: cardSetPayload,
          card_number: cardNumberPayload,
          condition: condition as any,
          card_language: isTradingCard ? (cardLanguage || null) : null,
          finish: isTradingCard ? (finish || null) : null,
          edition: isTradingCard ? (edition || null) : null,
          product_type: productType,
          accessory_category: isAccessory ? accessoryCategory : null,
          brand: isTradingCard ? null : (brandClean || null),
          category: mainCategory,
          subcategory: isGeneric
            ? subcategory
            : isSealed ? "sealed" : isAccessory ? "accessories" : cardType === "graded" ? "graded" : "raw",
          attributes: isGeneric ? cleanAttributes(attrFields, attrs) : {},
          product_line_set: isSealed ? lineSetClean : null,
          quantity: isSealed ? qtyNumber : null,
          compatible_game: isAccessory ? (compatibleGameClean || null) : null,
          grading_company: isTradingCard && cardType === "graded" ? grader : "RAW",
          grade: isTradingCard && cardType === "graded" ? gradeValue : "Ungraded",
          card_type: isTradingCard ? cardType : isSealed ? "sealed" : "raw",
          custom_grading_company:
            isTradingCard && cardType === "graded" && grader === "OTHER" ? customGrader.trim() : null,
          custom_grade:
            isTradingCard && cardType === "graded" && gradeValue === "Other" ? customGrade.trim() : null,
          grading_cert_number:
            isTradingCard && cardType === "graded" ? (certNumber.trim() || null) : null,
          cert_verified: Boolean(isTradingCard && cardType === "graded" && certMeta?.verified),
          cert_verification_status:
            isTradingCard && cardType === "graded" ? (certMeta?.status ?? null) : null,
          cert_verified_at:
            isTradingCard && cardType === "graded" ? (certMeta?.verified_at ?? null) : null,
          cert_official_url:
            isTradingCard && cardType === "graded" ? (certMeta?.official_url ?? null) : null,
          cert_verification_method:
            isTradingCard && cardType === "graded" ? (certMeta?.method ?? null) : null,

          trading_card_game: isTradingCard ? (tcgGame || null) : null,
          trading_card_game_other: isTradingCard && tcgGame === "other" ? tcgGameOther.trim() : null,
          rarity: isTradingCard ? (rarity || null) : null,
          rarity_other: isTradingCard && rarity === "other" ? rarityOther.trim() : null,
          grading_status: isTradingCard ? (cardType === "graded" ? "graded" : "raw") : null,
          is_autographed: isTradingCard ? isAutographed : false,
          autograph_details: isTradingCard && isAutographed ? (autographDetails.trim() || null) : null,
          is_serial_numbered: isTradingCard ? isSerialNumbered : false,
          serial_number: isTradingCard && isSerialNumbered ? serialNumber.trim() : null,
          starting_price_pence: Math.round(starting * 100),
          reserve_price_pence: reserve ? Math.round(reserve * 100) : null,
          buy_now_enabled: buyNowEnabled,
          buy_now_price_pence: buyNowEnabled ? Math.round((buyNow as number) * 100) : null,
          keep_buy_now_after_bid: buyNowEnabled ? keepBuyNowAfterBid : false,
          auto_relist_enabled: autoRelist,

          start_time: now.toISOString(),
          end_time: end.toISOString(),
          status: "pending",
          is_test: useTest,

        } as any)
        .select("id,title,grading_company,grade")
        .single();
      if (error) throw error;


      // Link the published listing to its permanent registry record. Never
      // fatal: the listing exists and can be relinked from the seller area.
      if (registryLink) {
        try {
          await linkRegistryFn({
            data: {
              listingId: data.id,
              provider: registryLink.provider,
              certNumber: registryLink.cert,
              sellerConfirmed: registryConfirmed,
            },
          });
        } catch {
          /* non-fatal */
        }
      }

      // Kick off the advisory AI condition inspection in the background. The
      // listing is already live; a failed inspection must never block it.
      try {
        void runInspectionFn({ data: { auctionId: data.id } }).catch(() => {});
      } catch {
        /* non-fatal */
      }

      return data;
    },

    onSuccess: (data: { id: string; title: string | null; grading_company: string | null; grade: string | null }) => {
      toast.success("Auction listed!");
      navigate({
        to: "/auction/$slug",
        params: { slug: buildAuctionSlug(data) },
      });
    },
    onError: (e: Error) => {
      if (/listing_limit_reached/i.test(e.message)) {
        toast.error("You've reached the active-listing limit. Please close or end existing listings before publishing more.");
        return;
      }
      toast.error(e.message);
    },
  });

  // Never optimistically render the listing form. Wait for auth + profile query to resolve.
  if (authLoading || !user || profilePending || isFetching) {
    return (
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-24 flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!verified) {
    return (
      <ProfileCompletionGate>
        <StripeGate userId={user.id} profile={sellerProfile ?? null} onRefreshed={() => refetch()} />
      </ProfileCompletionGate>
    );
  }


  return (
    <ProfileCompletionGate>
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-12">

      <div className="mb-8">
        <p className="text-xs uppercase tracking-widest text-gold">Seller</p>
        <h1 className="mt-2 font-display text-4xl">Create a listing</h1>
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); mutation.mutate(new FormData(e.currentTarget)); }}
        className="space-y-6 rounded-2xl border border-border/60 bg-card p-6 gold-ring"
      >
        <div>
          <Label>Images (up to 8)</Label>
          <div className="mt-2">
            <StudioUploader
              assets={studioAssets}
              onChange={setStudioAssets}
              userId={user.id}
              max={8}
              disabled={mutation.isPending}
              debug={isAdmin && imagingDebug}
            />
          </div>
          {studioAssets.length > 0 && (
            <Suspense fallback={null}>
              <ProcessingTimeline className="mt-3" />
            </Suspense>
          )}

          {productType === "trading_card" && scanSourceFile && (
            <div className="mt-3 space-y-3">
              <Suspense fallback={null}>
                <AiScanPanel
                  file={scanSourceFile}
                  revision={scanRevision}
                  disabled={mutation.isPending}
                  onApply={applyScanIdentity}
                />
              </Suspense>
              <Suspense fallback={null}>
                <AiGradePanel
                  file={scanSourceFile}
                  revision={scanRevision}
                  game={tcgGame || null}
                  disabled={mutation.isPending}
                  onApply={applyGradeEstimate}
                />
              </Suspense>
            </div>
          )}

        </div>



        {(productType === "trading_card" || productType === "sealed_product") && (<>
        <div className="rounded-lg border border-gold/30 bg-gold/5 p-3 flex items-center justify-between gap-3">
          <div className="text-sm">
            <div className="font-medium flex items-center gap-2"><Sparkles className="h-4 w-4 text-gold" /> Scan card with AI</div>
            <div className="text-xs text-muted-foreground">Optional. AI suggests card details and a condition estimate — you review and confirm before anything fills the form.</div>
          </div>
          <Button type="button" variant="outline" className="border-gold/40" onClick={() => setScanOpen(true)}>
            Scan card with AI
          </Button>
        </div>

        <PriceEstimatePanel
          dimensions={{
            game: "pokemon",
            card_name: title || null,
            card_set: cardSet || null,
            card_number: cardNumber || null,
            language: null,
            variant: null,
            is_graded: grader !== "RAW",
            grading_company: grader === "RAW" ? null : grader,
            grade: grade || null,
            condition: condition || null,
          }}
          onApplyStart={(pence) => setStartingPricePounds((pence / 100).toFixed(2))}
          onApplyBuyNow={(pence) => { setBuyNowEnabled(true); setBuyNowPounds((pence / 100).toFixed(2)); }}
        />
        </>)}

        <div>
          <Label htmlFor="title">Title *</Label>
          <Input
            id="title"
            name="title"
            required
            placeholder="Charizard 1st Edition Base Set Holo"
            value={title}
            onChange={(e) => { setTitle(e.target.value); markDirty("title"); }}
          />
        </div>

        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="description">Description</Label>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="border-gold/40 h-7 text-xs"
              onClick={handleAiRewrite}
              disabled={rewriting}
            >
              {rewriting ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Sparkles className="h-3 w-3 mr-1" /> AI rewrite</>}
            </Button>
          </div>
          <Textarea
            id="description"
            name="description"
            rows={4}
            placeholder="Condition notes, provenance, centering..."
            value={description}
            onChange={(e) => { setDescription(e.target.value); markDirty("description"); }}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label>Product type *</Label>
            <Select value={productType} onValueChange={(v) => { setProductType(v as ProductType); setSubcategory(""); setAttrs({}); }}>
              <SelectTrigger className="min-h-[44px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRODUCT_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>{PRODUCT_TYPE_LABEL[t]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-muted-foreground">
              {productType === "trading_card" && "Individual singles (raw or graded)."}
              {productType === "sealed_product" && "Sealed booster boxes, packs, decks, tins and similar factory-sealed product."}
              {productType === "accessory" && "Storage, protection and display supplies. Grading fields are hidden."}
              {productType === "collectible" && "Funko Pop!, figures, statues, plush, pins and other collectibles."}
              {productType === "fashion" && "Clothing, sneakers, hats, bags and fashion accessories."}
            </p>
          </div>

          {(productType === "collectible" || productType === "fashion") && (() => {
            const cat = categoryForProductType(productType);
            const subs = subcategoriesFor(cat);
            const groups = [...new Set(subs.map((x) => x.group ?? ""))];
            const fields = attributeFieldsFor(cat, subcategory);
            const setAttr = (k: string, v: string | boolean) => setAttrs((prev) => ({ ...prev, [k]: v }));
            return (
              <>
                <div>
                  <Label>Subcategory *</Label>
                  <Select value={subcategory} onValueChange={(v) => { setSubcategory(v); setAttrs({}); }}>
                    <SelectTrigger className="min-h-[44px]"><SelectValue placeholder="Choose a subcategory" /></SelectTrigger>
                    <SelectContent>
                      {groups.map((g) => (
                        <div key={g || "all"}>
                          {g && <div className="px-2 py-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">{g}</div>}
                          {subs.filter((x) => (x.group ?? "") === g).map((x) => (
                            <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>
                          ))}
                        </div>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="generic_brand">{productType === "fashion" ? "Brand *" : "Brand / Manufacturer"}</Label>
                  <Input id="generic_brand" className="min-h-[44px]" value={brand} onChange={(e) => setBrand(e.target.value)} placeholder={productType === "fashion" ? "e.g. Nike, Supreme" : "e.g. Funko, Bandai"} />
                </div>
                {fields.map((f) => (
                  <div key={f.key} className={f.type === "boolean" ? "flex items-end" : ""}>
                    {f.type === "boolean" ? (
                      <label className="flex min-h-[44px] items-center gap-3 cursor-pointer text-sm">
                        <input type="checkbox" className="h-4 w-4 accent-primary" checked={attrs[f.key] === true} onChange={(e) => setAttr(f.key, e.target.checked)} />
                        {f.label}
                      </label>
                    ) : (
                      <>
                        <Label htmlFor={`attr_${f.key}`}>{f.label}{f.required ? " *" : ""}</Label>
                        {f.type === "select" ? (
                          <Select value={(attrs[f.key] as string) ?? ""} onValueChange={(v) => setAttr(f.key, v)}>
                            <SelectTrigger id={`attr_${f.key}`} className="min-h-[44px]"><SelectValue placeholder={`Select ${f.label.toLowerCase()}`} /></SelectTrigger>
                            <SelectContent>
                              {f.options!.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input id={`attr_${f.key}`} className="min-h-[44px]" value={(attrs[f.key] as string) ?? ""} placeholder={f.placeholder} onChange={(e) => setAttr(f.key, e.target.value)} />
                        )}
                      </>
                    )}
                  </div>
                ))}
              </>
            );
          })()}

          {productType === "trading_card" && (
            <>
              <div className="sm:col-span-2">
                <Label>Trading Card Game *</Label>
                <Select value={tcgGame} onValueChange={setTcgGame}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TCG_GAMES.map((g) => (
                      <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {tcgGame === "other" && (
                  <Input
                    className="mt-2"
                    value={tcgGameOther}
                    onChange={(e) => setTcgGameOther(e.target.value)}
                    placeholder="Enter trading card game"
                    aria-label="Enter trading card game"
                    required
                  />
                )}
              </div>
              <div>
                <Label htmlFor="card_set">Card set *</Label>
                <Input
                  id="card_set"
                  name="card_set"
                  required
                  placeholder="Base Set"
                  value={cardSet}
                  onChange={(e) => { setCardSet(e.target.value); markDirty("card_set"); }}
                />
              </div>
              <div>
                <Label htmlFor="card_number">Card # *</Label>
                <Input
                  id="card_number"
                  name="card_number"
                  required
                  placeholder="4/102"
                  value={cardNumber}
                  onChange={(e) => { setCardNumber(e.target.value); markDirty("card_number"); }}
                />
              </div>
              <div>
                <Label>Card language *</Label>
                <Select value={cardLanguage} onValueChange={setCardLanguage}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CARD_LANGUAGES.map((l) => (
                      <SelectItem key={l} value={l}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Finish *</Label>
                <Select value={finish || "__none__"} onValueChange={(v) => setFinish(v === "__none__" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="Select finish" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None —</SelectItem>
                    {FINISH_OPTIONS.map((f) => (
                      <SelectItem key={f} value={f}>{f}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Edition</Label>
                <Select value={edition} onValueChange={setEdition}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EDITION_OPTIONS.map((e) => (
                      <SelectItem key={e} value={e}>{e}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label>Rarity</Label>
                <Select value={rarity || "__none__"} onValueChange={(v) => setRarity(v === "__none__" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="Select rarity (optional)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None —</SelectItem>
                    {RARITY_OPTIONS.map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {rarity === "other" && (
                  <Input
                    className="mt-2"
                    value={rarityOther}
                    onChange={(e) => setRarityOther(e.target.value)}
                    placeholder="Enter rarity"
                    aria-label="Enter rarity"
                    required
                  />
                )}
              </div>
              <div className="sm:col-span-2 rounded-lg border border-border/60 bg-surface-2/40 p-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isAutographed}
                    onChange={(e) => setIsAutographed(e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-gold"
                  />
                  <span className="text-sm">Autographed card</span>
                </label>
                {isAutographed && (
                  <div className="mt-2">
                    <Label htmlFor="autograph_details" className="text-xs">Autograph details (optional)</Label>
                    <Input
                      id="autograph_details"
                      value={autographDetails}
                      onChange={(e) => setAutographDetails(e.target.value)}
                      placeholder="e.g. On-card auto, signed by artist"
                    />
                  </div>
                )}
              </div>
              <div className="sm:col-span-2 rounded-lg border border-border/60 bg-surface-2/40 p-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isSerialNumbered}
                    onChange={(e) => setIsSerialNumbered(e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-gold"
                  />
                  <span className="text-sm">Serial numbered card</span>
                </label>
                {isSerialNumbered && (
                  <div className="mt-2">
                    <Label htmlFor="serial_number" className="text-xs">Serial number *</Label>
                    <Input
                      id="serial_number"
                      value={serialNumber}
                      onChange={(e) => setSerialNumber(e.target.value)}
                      placeholder="e.g. 12/99, 1/1"
                      required
                    />
                  </div>
                )}
              </div>
            </>
          )}



          {productType === "sealed_product" && (
            <>
              <div>
                <Label htmlFor="product_line_set">Product line / set *</Label>
                <Input
                  id="product_line_set"
                  value={productLineSet}
                  onChange={(e) => setProductLineSet(e.target.value)}
                  placeholder="Scarlet & Violet — Twilight Masquerade"
                  required
                />
              </div>
              <div>
                <Label htmlFor="brand">Brand *</Label>
                <BrandCombobox id="brand" value={brand} onChange={setBrand} required ariaLabel="Brand" />
              </div>
              <div>
                <Label htmlFor="quantity">Quantity *</Label>
                <Input
                  id="quantity"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  step={1}
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  required
                />
              </div>
            </>
          )}

          {productType === "accessory" && (
            <>
              <div>
                <Label htmlFor="brand">Brand *</Label>
                <BrandCombobox id="brand" value={brand} onChange={setBrand} required ariaLabel="Brand" />
              </div>
              <div>
                <Label>Accessory category *</Label>
                <Select value={accessoryCategory} onValueChange={(v) => setAccessoryCategory(v as AccessoryCategory)}>
                  <SelectTrigger className="min-h-[44px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ACCESSORY_CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>{ACCESSORY_CATEGORY_LABEL[c]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label>Compatible game (optional)</Label>
                <Select value={compatibleGame || "none"} onValueChange={(v) => setCompatibleGame(v === "none" ? "" : v)}>
                  <SelectTrigger className="min-h-[44px]"><SelectValue placeholder="Any / Universal" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not specified</SelectItem>
                    {COMPATIBLE_GAMES.map((g) => (
                      <SelectItem key={g} value={g}>{g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {compatibleGame === "Other" && (
                  <Input
                    className="mt-2"
                    value={customCompatibleGame}
                    onChange={(e) => setCustomCompatibleGame(e.target.value)}
                    placeholder="Enter compatible game"
                    aria-label="Enter compatible game"
                    required
                  />
                )}
              </div>
            </>
          )}

          <div className={productType === "trading_card" ? "" : "sm:col-span-2"}>
            <Label>Condition *</Label>
            <Select value={condition} onValueChange={(v) => { setCondition(v); markDirty("condition_form"); }}>
              <SelectTrigger className="min-h-[44px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(conditionsForProductType(productType) as readonly string[]).map((c) => (
                  <SelectItem key={c} value={c}>{CONDITION_LABEL[c as keyof typeof CONDITION_LABEL] ?? c.replace(/_/g, " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {productType === "trading_card" && (
            <div className="sm:col-span-2">
              <Label>Card type</Label>
              <Select value={cardType} onValueChange={(v) => setCardType(v as CardType)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CARD_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>{CARD_TYPE_LABEL[t]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-xs text-muted-foreground">
                {cardType === "graded" && "Graded listings require a grading company and grade."}
                {cardType === "raw" && "Raw (ungraded) cards. No grading company or grade required."}
                {cardType === "sealed" && "Sealed products (booster boxes, packs, tins, decks). No grading applies."}
              </p>
            </div>
          )}

          {productType === "trading_card" && cardType === "graded" && (() => {
            const supportsApiVerify = isApiGrader(grader);
            const canAutoVerify = supportsCertVerification(grader);
            return (
              <div className="sm:col-span-2 rounded-lg border border-border/60 bg-surface-2/50 p-3">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">Verify grading certificate (optional)</Label>
                  {!supportsApiVerify && (
                    <span className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] uppercase tracking-widest text-muted-foreground">
                      Manual Entry
                    </span>
                  )}
                </div>
                {supportsApiVerify ? (
                  <>
                    <p className="mt-1 mb-2 text-xs text-muted-foreground">
                      Look up your PSA / BGS / CGC / SGC / ACE / TAG cert number. On success we prefill the grader, grade, and title so buyers can independently re-verify before bidding.
                    </p>
                    <GradingCertLookup
                      compact
                      initialCompany={grader.toLowerCase() as ApiGrader}
                      initialCert={certNumber}
                      onResult={(r) => {
                        setCertNumber(r.cert_number);
                        setCertVerified(true);
                        setCertMeta({
                          verified: r.verified,
                          status: "verified",
                          verified_at: r.verified_at,
                          official_url: r.official_url,
                          method: r.method,
                        });
                        const company = r.company.toUpperCase();
                        if (isApiGrader(company)) {
                          setGrader(company as Grader);
                          markDirty("grading_company");
                        }
                        if (r.grade) { setGrade(r.grade); markDirty("grade"); }
                        if (r.card_name && !title) { setTitle(r.card_name); markDirty("title"); }
                        if (r.set_name && !cardSet) { setCardSet(r.set_name); markDirty("card_set"); }
                        // Surface the permanent registry record straight away.
                        setRegistryConfirmed(false);
                        void checkRegistryFn({
                          data: {
                            provider: r.company.toLowerCase(),
                            certNumber: r.cert_number,
                            listing: {
                              card_name: title || r.card_name,
                              set_name: cardSet || r.set_name,
                              grade: r.grade,
                            },
                          },
                        })
                          .then((c) => setRegistryCheck(c as RegistryCheck))
                          .catch(() => setRegistryCheck(null));
                      }}
                    />

                  </>
                ) : (
                  <p className="mt-1 mb-2 text-xs text-muted-foreground">
                    Automatic verification is not available for this grading company. Please enter the grading details manually.
                  </p>
                )}
                <div className="mt-2">
                  <Label htmlFor="grading_cert_number" className="text-xs">Certification number{supportsApiVerify ? "" : " (optional)"}</Label>
                  <Input
                    id="grading_cert_number"
                    value={certNumber}
                    onChange={(e) => { setCertNumber(e.target.value); setCertVerified(false); setCertMeta(null); setRegistryCheck(null); setRegistryConfirmed(false); }}
                    placeholder="e.g. 00012345678"
                  />
                  {certVerified && canAutoVerify && (
                    <p className="mt-1 text-xs text-emerald-500">Certificate verified — will be shown to buyers.</p>
                  )}
                </div>

                {registryCheck?.found && (
                  <div className="mt-3 rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs">
                    <p className="font-medium text-emerald-200">
                      Found in the BidNova Verified Card Registry
                    </p>
                    <p className="mt-1 text-muted-foreground">
                      {[registryCheck.cardName, registryCheck.setName, registryCheck.grade ? `Grade ${registryCheck.grade}` : null]
                        .filter(Boolean)
                        .join(" · ") || "Permanent record available"}
                    </p>
                    {registryCheck.registryPath && (
                      <Link
                        to={registryCheck.registryPath}
                        className="mt-1 inline-flex items-center gap-1 text-emerald-300 underline underline-offset-2"
                      >
                        View permanent registry record
                      </Link>
                    )}
                    {registryCheck.duplicateActiveListings > 0 && (
                      <p className="mt-2 rounded bg-amber-500/10 p-2 text-amber-200">
                        This certificate is already linked to {registryCheck.duplicateActiveListings}{" "}
                        other active listing{registryCheck.duplicateActiveListings > 1 ? "s" : ""}.
                        Duplicate certificates are reviewed by BidNova.
                      </p>
                    )}
                    {registryCheck.mismatches.length > 0 && (
                      <div className="mt-2 rounded bg-amber-500/10 p-2 text-amber-100">
                        <p className="font-medium">Your details differ from the official record:</p>
                        <ul className="mt-1 list-disc space-y-0.5 pl-4">
                          {registryCheck.mismatches.map((m) => (
                            <li key={m.field}>
                              <span className="capitalize">{m.field.replace(/_/g, " ")}</span>: you
                              entered “{m.listing}”, the record says “{m.registry}”.
                            </li>
                          ))}
                        </ul>
                        <label className="mt-2 flex items-start gap-2">
                          <input
                            type="checkbox"
                            className="mt-0.5"
                            checked={registryConfirmed}
                            onChange={(e) => setRegistryConfirmed(e.target.checked)}
                          />
                          <span>
                            I confirm my listing details are correct for the card I am selling.
                          </span>
                        </label>
                      </div>
                    )}
                  </div>
                )}
              </div>

            );
          })()}
          {productType === "trading_card" && cardType === "graded" && (
            <>
              <div>
                <Label>Grading company</Label>
                <Select
                  value={grader}
                  onValueChange={(v) => { setGrader(v as Grader); markDirty("grading_company"); }}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SELLER_GRADING_COMPANIES.map((g) => (
                      <SelectItem key={g} value={g}>{g === "OTHER" ? "Others" : (GRADER_LABEL[g as Grader] ?? g)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {grader === "OTHER" && (
                  <Input
                    className="mt-2"
                    value={customGrader}
                    onChange={(e) => setCustomGrader(e.target.value)}
                    placeholder="Enter grading company"
                    required
                    aria-label="Enter grading company"
                  />
                )}
              </div>
              <div>
                <Label>Grade</Label>
                <Select value={grade || "Ungraded"} onValueChange={(v) => { setGrade(v); markDirty("grade"); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SELLER_GRADE_OPTIONS.map((g) => (
                      <SelectItem key={g} value={g}>{formatGradeLabel(g)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {grade === "Other" && (
                  <Input
                    className="mt-2"
                    value={customGrade}
                    onChange={(e) => setCustomGrade(e.target.value)}
                    placeholder="Enter grade"
                    required
                    aria-label="Enter grade"
                  />
                )}
              </div>
            </>
          )}
          <div>
            <Label>Auction duration</Label>
            <Select value={String(durationHours)} onValueChange={(v) => setDurationHours(Number(v))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {DURATIONS.map((d) => <SelectItem key={d.hours} value={String(d.hours)}>{d.label}</SelectItem>)}
                {isAdmin && isTest && ADMIN_TEST_DURATIONS.map((d) => (
                  <SelectItem key={d.hours} value={String(d.hours)}>{d.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {isAdmin && (
            <div className="sm:col-span-2 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isTest}
                  onChange={(e) => {
                    const on = e.target.checked;
                    setIsTest(on);
                    // Reset to a safe default when toggling off so a test duration doesn't leak.
                    if (!on && durationHours < 24) setDurationHours(72);
                  }}
                  className="mt-0.5 h-4 w-4 accent-amber-500"
                />
                <div className="text-sm">
                  <div className="font-medium text-amber-300">Admin test auction</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Enables sub-24h durations (min 5 min) for end-to-end testing. Listing will be prefixed <span className="font-mono">[TEST]</span> and flagged as test data. Not visible to normal sellers.
                  </div>
                </div>
              </label>
            </div>
          )}
          <div>
            <Label htmlFor="starting_price">Starting price (£) *</Label>
            <Input
              id="starting_price"
              name="starting_price"
              type="number"
              step="0.01"
              min="0.01"
              required
              value={startingPricePounds}
              onChange={(e) => setStartingPricePounds(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="reserve_price">Reserve price (£)</Label>
            <Input id="reserve_price" name="reserve_price" type="number" step="0.01" min="0" placeholder="Optional" />
          </div>
        </div>

        <div className="rounded-lg border border-border/60 bg-surface-2/40 p-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={autoRelist}
              onChange={(e) => setAutoRelist(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-gold"
            />
            <div className="text-sm flex-1">
              <div className="font-medium">Automatically relist if unsold</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                If the auction ends with no winning buyer, it restarts with the same details and duration. Sold items never relist.
              </div>
            </div>
          </label>
        </div>

        <div className="rounded-lg border border-border/60 bg-surface-2/40 p-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={buyNowEnabled}
              onChange={(e) => setBuyNowEnabled(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-gold"
            />
            <div className="text-sm flex-1">
              <div className="font-medium">Enable Buy It Now</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Let buyers purchase instantly at a fixed price and skip bidding. The price locks once the first bid is placed.
              </div>
            </div>
          </label>
          {buyNowEnabled && (
            <div className="mt-3 space-y-3">
              <div>
                <Label htmlFor="buy_now_price">Buy It Now price (£) *</Label>
                <Input
                  id="buy_now_price"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={buyNowPounds}
                  onChange={(e) => setBuyNowPounds(e.target.value)}
                  placeholder="Must be higher than the starting price"
                  required
                />
              </div>
              <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-border/60 bg-background/40 p-3">
                <input
                  type="checkbox"
                  checked={keepBuyNowAfterBid}
                  onChange={(e) => setKeepBuyNowAfterBid(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-gold"
                />
                <div className="text-sm flex-1">
                  <div className="font-medium">Keep Buy It Now available after bidding starts</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    If off, Buy It Now disappears once the first valid bid is placed. This setting locks after the first bid.
                  </div>
                </div>
              </label>
            </div>
          )}

        </div>

        <ShippingRecommendationCard
          graded={productType === "trading_card" && grader !== "RAW"}
          estimatedValuePence={Math.round((Number(startingPricePounds) || 0) * 100)}
          isSealedProduct={false}
        />

        <BundleSuggestionsPanel />


        <div className="flex gap-3">
          <Button asChild variant="outline" className="border-border/60"><Link to="/dashboard">Cancel</Link></Button>
          <Button type="submit" disabled={mutation.isPending} className="flex-1 bg-gold-gradient hover:opacity-90">
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Publish auction"}
          </Button>
        </div>
      </form>

      {scanOpen && (
      <Suspense fallback={null}>
      <ScanCardDialog
        open={scanOpen}
        onOpenChange={setScanOpen}
        dirtyFields={dirtyFields}
        onConfirm={(fields) => {
          if (fields.title !== undefined) setTitle(fields.title);
          if (fields.description !== undefined) setDescription((prev) => prev ? `${prev}\n\n${fields.description ?? ""}` : (fields.description ?? ""));
          if (fields.card_set !== undefined) setCardSet(fields.card_set);
          if (fields.card_number !== undefined) setCardNumber(fields.card_number);
          if (fields.grading_company !== undefined) {
            const g = fields.grading_company.toUpperCase();
            setGrader((ALL_GRADERS as readonly string[]).includes(g) ? (g as Grader) : "OTHER");
            if (!(ALL_GRADERS as readonly string[]).includes(g)) setCustomGrader(fields.grading_company);
          }
          if (fields.grade !== undefined) setGrade(fields.grade);
          if (fields.condition_form) setCondition(fields.condition_form);
          // fields.tags is captured server-side on the scan record — no tag input in the form yet.
        }}
      />
      </Suspense>
      )}

    </div>
    </ProfileCompletionGate>
  );
}

